import { Alert } from "@mui/material";

import { AutocompleteType } from "@/shared/components/TagAndMetadataAutocomplete";
import { type MetadataChangeset } from "@/shared/domain/metadataChangeset";
import { userFacingErrorMessage } from "@/shared/http";

import { MetadataForm } from "./MetadataForm";
import { MetadataView } from "./MetadataView";
import { type Metadata, type MetadataDiff } from "./types";

interface MetadataChangesetEditorProps {
  metadata: Metadata;
  /** Identifies the record being edited, so switching records resets the inputs. */
  recordKey: string;
  /** Sends each edit as a metadata changeset. */
  onChangeset: (changeset: MetadataChangeset) => void;
  isPending: boolean;
  error: Error | null;
  onDismissError: () => void;
  readonly?: boolean;
}

/**
 * Edits the metadata of any record whose update takes a metadata changeset:
 * adding a key, editing keys in the JSON editor and deleting a key each send
 * one changeset.
 */
export function MetadataChangesetEditor({
  metadata,
  recordKey,
  onChangeset,
  isPending,
  error,
  onDismissError,
  readonly = false,
}: MetadataChangesetEditorProps) {
  const updateMetadata = (nextMetadata: Metadata, diff?: MetadataDiff) => {
    // Edits made in the JSON editor arrive as `keysUpdated`. Deletes are left
    // to `onDeleteMetadata`: one click on a key's delete button makes
    // MetadataView fire both this callback and `onDeleteMetadata`, so acting
    // on `keysDeleted` here would send two requests for one click.
    if (!diff?.keysUpdated?.length) {
      return;
    }
    const putFields: Record<string, unknown> = {};
    for (const key of diff.keysUpdated) {
      putFields[key] = nextMetadata[key];
    }
    onChangeset({ put_fields: putFields });
  };

  return (
    <>
      {readonly && Object.keys(metadata).length === 0 && <>None</>}
      <MetadataView
        key={recordKey}
        metadata={metadata}
        onChange={updateMetadata}
        onDeleteMetadata={(key) => onChangeset({ remove_fields: [key] })}
        // Suppresses both the editor and the delete button inside MetadataView,
        // so the change and delete handlers need no readonly guard of their own.
        disabled={readonly || isPending}
      />
      {!readonly && (
        <MetadataForm
          key={`${recordKey}-form`}
          metadata={metadata}
          autocompleteType={AutocompleteType.GenericMetadataKeys}
          onAddMetadata={(key, value) =>
            onChangeset({ put_fields: { [key]: value } })
          }
          disabled={isPending}
          isLoading={isPending}
        />
      )}

      {error && (
        <Alert
          onClose={onDismissError}
          severity="error"
          style={{ marginTop: "var(--spacing-xs)" }}
        >
          {userFacingErrorMessage(error)}
        </Alert>
      )}
    </>
  );
}
